import Vue from 'vue'
import Vuex from 'vuex'
import api from '../services/api'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    books: [],
    wishlist: [],
    cart: [],

    currentUser: null,

    darkMode: false,

    loading: false,
    error: null
  },

  getters: {
    // -------------------------
    // Authentication
    // -------------------------

    isAuthenticated(state) {
      return !!state.currentUser
    },

    currentUser(state) {
      return state.currentUser
    },

    userRole(state) {
      return state.currentUser
        ? state.currentUser.role
        : null
    },

    isBuyer(state) {
      return (
        state.currentUser &&
        state.currentUser.role === 'buyer'
      )
    },

    isSeller(state) {
      return (
        state.currentUser &&
        state.currentUser.role === 'seller'
      )
    },

    // -------------------------
    // Books
    // -------------------------

    booksCount(state) {
      return state.books.length
    },

    categoriesCount(state) {
      return new Set(
        state.books.map(
          book => book.category
        )
      ).size
    },

    averagePrice(state) {
      if (state.books.length === 0) {
        return 0
      }

      const total = state.books.reduce(
        (sum, book) =>
          sum + Number(book.price),
        0
      )

      return total / state.books.length
    },

    // -------------------------
    // Wishlist
    // -------------------------

    wishlistCount(state) {
      return state.wishlist.length
    },

    // -------------------------
    // Cart
    // -------------------------

    cartCount(state) {
      return state.cart.reduce(
        (total, item) =>
          total + item.quantity,
        0
      )
    },

    cartSubtotal(state) {
      return state.cart.reduce(
        (total, item) =>
          total +
          Number(item.price) *
            item.quantity,
        0
      )
    },

    shippingCost(state, getters) {
      if (
        getters.cartCount === 0
      ) {
        return 0
      }

      return getters.cartSubtotal >= 300
        ? 0
        : 25
    },

    cartTotal(state, getters) {
      return (
        getters.cartSubtotal +
        getters.shippingCost
      )
    },

    isInCart: state => ISBN => {
      return state.cart.some(
        item => item.ISBN === ISBN
      )
    },

    cartItemQuantity: state => ISBN => {
      const item = state.cart.find(
        item => item.ISBN === ISBN
      )

      return item
        ? item.quantity
        : 0
    }
  },

  mutations: {
    // -------------------------
    // Books
    // -------------------------

    SET_BOOKS(state, books) {
      state.books = books
    },

    SET_LOADING(state, value) {
      state.loading = value
    },

    SET_ERROR(state, value) {
      state.error = value
    },

    ADD_BOOK(state, book) {
      state.books.push(book)
    },

    UPDATE_BOOK(state, updatedBook) {
      const index =
        state.books.findIndex(
          book =>
            book.id ===
            updatedBook.id
        )

      if (index !== -1) {
        Vue.set(
          state.books,
          index,
          updatedBook
        )
      }
    },

    DELETE_BOOK(state, id) {
      state.books =
        state.books.filter(
          book => book.id !== id
        )

      state.wishlist =
        state.wishlist.filter(
          book => book.id !== id
        )

      state.cart =
        state.cart.filter(
          item => item.id !== id
        )

      localStorage.setItem(
        'wishlist',
        JSON.stringify(
          state.wishlist
        )
      )

      localStorage.setItem(
        'cart',
        JSON.stringify(
          state.cart
        )
      )
    },

    // -------------------------
    // Authentication
    // -------------------------

    SET_CURRENT_USER(state, user) {
      state.currentUser = user

      if (user) {
        localStorage.setItem(
          'currentUser',
          JSON.stringify(user)
        )
      } else {
        localStorage.removeItem(
          'currentUser'
        )
      }
    },

    // -------------------------
    // Wishlist
    // -------------------------

    SET_WISHLIST(state, wishlist) {
      state.wishlist = wishlist

      localStorage.setItem(
        'wishlist',
        JSON.stringify(
          wishlist
        )
      )
    },

    ADD_TO_WISHLIST(state, book) {
      state.wishlist.push(book)

      localStorage.setItem(
        'wishlist',
        JSON.stringify(
          state.wishlist
        )
      )
    },

    REMOVE_FROM_WISHLIST(
      state,
      index
    ) {
      state.wishlist.splice(
        index,
        1
      )

      localStorage.setItem(
        'wishlist',
        JSON.stringify(
          state.wishlist
        )
      )
    },

    // -------------------------
    // Cart
    // -------------------------

    SET_CART(state, cart) {
      state.cart = cart

      localStorage.setItem(
        'cart',
        JSON.stringify(cart)
      )
    },

    ADD_TO_CART(state, book) {
      const existingItem =
        state.cart.find(
          item =>
            item.ISBN === book.ISBN
        )

      if (existingItem) {
        existingItem.quantity += 1
      } else {
        state.cart.push({
          ...book,
          quantity: 1
        })
      }

      localStorage.setItem(
        'cart',
        JSON.stringify(
          state.cart
        )
      )
    },

    INCREASE_CART_ITEM(
      state,
      ISBN
    ) {
      const item =
        state.cart.find(
          item =>
            item.ISBN === ISBN
        )

      if (item) {
        item.quantity += 1
      }

      localStorage.setItem(
        'cart',
        JSON.stringify(
          state.cart
        )
      )
    },

    DECREASE_CART_ITEM(
      state,
      ISBN
    ) {
      const item =
        state.cart.find(
          item =>
            item.ISBN === ISBN
        )

      if (!item) {
        return
      }

      if (item.quantity > 1) {
        item.quantity -= 1
      } else {
        state.cart =
          state.cart.filter(
            item =>
              item.ISBN !== ISBN
          )
      }

      localStorage.setItem(
        'cart',
        JSON.stringify(
          state.cart
        )
      )
    },

    REMOVE_FROM_CART(
      state,
      ISBN
    ) {
      state.cart =
        state.cart.filter(
          item =>
            item.ISBN !== ISBN
        )

      localStorage.setItem(
        'cart',
        JSON.stringify(
          state.cart
        )
      )
    },

    CLEAR_CART(state) {
      state.cart = []

      localStorage.removeItem(
        'cart'
      )
    },

    // -------------------------
    // Dark Mode
    // -------------------------

    SET_DARK_MODE(
      state,
      value
    ) {
      state.darkMode = value

      localStorage.setItem(
        'darkMode',
        JSON.stringify(value)
      )
    }
  },

  actions: {
    // -------------------------
    // Books
    // -------------------------

    async fetchBooks({
      commit
    }) {
      commit(
        'SET_LOADING',
        true
      )

      commit(
        'SET_ERROR',
        null
      )

      try {
        const response =
          await api.get('/books')

        commit(
          'SET_BOOKS',
          response.data
        )
      } catch (error) {
        console.error(
          'Error loading books:',
          error
        )

        commit(
          'SET_ERROR',
          'Unable to load books.'
        )
      } finally {
        commit(
          'SET_LOADING',
          false
        )
      }
    },

    async addBook(
      {
        commit,
        getters
      },
      book
    ) {
      if (!getters.isSeller) {
        return {
          success: false,
          message:
            'Only sellers can add books.'
        }
      }

      try {
        const response =
          await api.post(
            '/books',
            book
          )

        commit(
          'ADD_BOOK',
          response.data
        )

        return {
          success: true,
          data:
            response.data
        }
      } catch (error) {
        console.error(
          'Error adding book:',
          error
        )

        return {
          success: false,
          message:
            'Failed to add book.'
        }
      }
    },

    async updateBook(
      {
        commit,
        getters
      },
      book
    ) {
      if (!getters.isSeller) {
        return {
          success: false,
          message:
            'Only sellers can edit books.'
        }
      }

      try {
        const response =
          await api.put(
            `/books/${book.id}`,
            book
          )

        commit(
          'UPDATE_BOOK',
          response.data
        )

        return {
          success: true,
          data:
            response.data
        }
      } catch (error) {
        console.error(
          'Error updating book:',
          error
        )

        return {
          success: false,
          message:
            'Failed to update book.'
        }
      }
    },

    async deleteBook(
      {
        commit,
        getters
      },
      id
    ) {
      if (!getters.isSeller) {
        return {
          success: false,
          message:
            'Only sellers can delete books.'
        }
      }

      try {
        await api.delete(
          `/books/${id}`
        )

        commit(
          'DELETE_BOOK',
          id
        )

        return {
          success: true
        }
      } catch (error) {
        console.error(
          'Error deleting book:',
          error
        )

        return {
          success: false,
          message:
            'Failed to delete book.'
        }
      }
    },

    // -------------------------
    // Authentication
    // -------------------------

    initAuth({
      commit
    }) {
      const savedUser =
        localStorage.getItem(
          'currentUser'
        )

      if (!savedUser) {
        return
      }

      try {
        const user =
          JSON.parse(savedUser)

        commit(
          'SET_CURRENT_USER',
          user
        )
      } catch (error) {
        localStorage.removeItem(
          'currentUser'
        )
      }
    },

    register(
      { commit },
      user
    ) {
      const users =
        JSON.parse(
          localStorage.getItem(
            'users'
          )
        ) || []

      const exists =
        users.some(
          existingUser =>
            existingUser.email ===
            user.email
        )

      if (exists) {
        return {
          success: false,
          message:
            'This email is already registered.'
        }
      }

      users.push(user)

      localStorage.setItem(
        'users',
        JSON.stringify(users)
      )

      commit(
        'SET_CURRENT_USER',
        user
      )

      return {
        success: true
      }
    },

    login(
      { commit },
      credentials
    ) {
      const users =
        JSON.parse(
          localStorage.getItem(
            'users'
          )
        ) || []

      const user =
        users.find(
          existingUser =>
            existingUser.email ===
              credentials.email &&
            existingUser.password ===
              credentials.password
        )

      if (!user) {
        return {
          success: false,
          message:
            'Invalid email or password.'
        }
      }

      commit(
        'SET_CURRENT_USER',
        user
      )

      return {
        success: true
      }
    },

    logout({ commit }) {
      commit(
        'SET_CURRENT_USER',
        null
      )
    },

    // -------------------------
    // Wishlist
    // -------------------------

    loadWishlist({
      commit
    }) {
      const savedWishlist =
        localStorage.getItem(
          'wishlist'
        )

      if (!savedWishlist) {
        commit(
          'SET_WISHLIST',
          []
        )

        return
      }

      try {
        const wishlist =
          JSON.parse(
            savedWishlist
          )

        if (
          Array.isArray(wishlist)
        ) {
          commit(
            'SET_WISHLIST',
            wishlist
          )
        } else {
          commit(
            'SET_WISHLIST',
            []
          )
        }
      } catch (error) {
        commit(
          'SET_WISHLIST',
          []
        )
      }
    },

    addToWishlist(
      {
        commit,
        state
      },
      book
    ) {
      const exists =
        state.wishlist.some(
          item =>
            item.ISBN ===
            book.ISBN
        )

      if (!exists) {
        commit(
          'ADD_TO_WISHLIST',
          book
        )
      }
    },

    removeFromWishlist(
      {
        commit
      },
      index
    ) {
      commit(
        'REMOVE_FROM_WISHLIST',
        index
      )
    },

    // -------------------------
    // Cart
    // -------------------------

    loadCart({
      commit
    }) {
      const savedCart =
        localStorage.getItem(
          'cart'
        )

      if (!savedCart) {
        commit(
          'SET_CART',
          []
        )

        return
      }

      try {
        const cart =
          JSON.parse(
            savedCart
          )

        if (
          Array.isArray(cart)
        ) {
          commit(
            'SET_CART',
            cart
          )
        } else {
          commit(
            'SET_CART',
            []
          )
        }
      } catch (error) {
        commit(
          'SET_CART',
          []
        )
      }
    },

    addToCart(
      {
        commit,
        getters
      },
      book
    ) {
      if (!getters.isBuyer) {
        return
      }

      commit(
        'ADD_TO_CART',
        book
      )
    },

    increaseCartItem(
      {
        commit
      },
      ISBN
    ) {
      commit(
        'INCREASE_CART_ITEM',
        ISBN
      )
    },

    decreaseCartItem(
      {
        commit
      },
      ISBN
    ) {
      commit(
        'DECREASE_CART_ITEM',
        ISBN
      )
    },

    removeFromCart(
      {
        commit
      },
      ISBN
    ) {
      commit(
        'REMOVE_FROM_CART',
        ISBN
      )
    },

    clearCart({
      commit
    }) {
      commit(
        'CLEAR_CART'
      )
    },

    // -------------------------
    // Dark Mode
    // -------------------------

    loadDarkMode({
      commit
    }) {
      const savedMode =
        localStorage.getItem(
          'darkMode'
        )

      if (savedMode !== null) {
        try {
          commit(
            'SET_DARK_MODE',
            JSON.parse(
              savedMode
            )
          )
        } catch (error) {
          commit(
            'SET_DARK_MODE',
            false
          )
        }
      }
    },

    toggleDarkMode({
      commit,
      state
    }) {
      commit(
        'SET_DARK_MODE',
        !state.darkMode
      )
    }
  }
})